// Pure helpers for the Features page (goals/admin-panel/DESIGN.md "Features
// and Platforms pages"): which rollup metrics make each feature's counts,
// people and adopters, and the adopters table.
import type { AnalyticsMetric, AnalyticsSeries } from "./analytics";
import { builtZeros, seriesFor } from "./growth";

/** One count series of a feature: a metric, optionally one value of one of its dims. */
export interface CountSource {
  label: string;
  metric: AnalyticsMetric;
  /** Only rows whose `dim` is `value` (the read groups by `dim`). */
  only?: { dim: string; value: string };
}

export interface Feature {
  /** The `feature` dim of `feature_users` and `feature_adopters`. */
  id: string;
  label: string;
  counts: readonly CountSource[];
  /** Whether `feature_users` counts the people using it per day. */
  daily: boolean;
  /** What makes someone an adopter, in words. */
  adopters: string;
  /** A snapshot metric of its own counting the adopters, in place of `feature_adopters`. */
  adoptersMetric?: AnalyticsMetric;
}

const LAST_30 = "in the last 30 days";

export const FEATURES: readonly Feature[] = [
  {
    id: "chat",
    label: "Chat",
    counts: [{ label: "Messages", metric: "messages" }],
    daily: true,
    adopters: `sent a message ${LAST_30}`,
  },
  {
    id: "media",
    label: "Photos, videos and files",
    counts: [{ label: "Media messages", metric: "messages", only: { dim: "kind", value: "media" } }],
    daily: true,
    adopters: `sent one ${LAST_30}`,
  },
  {
    id: "voice",
    label: "Voice messages",
    counts: [{ label: "Voice messages", metric: "messages", only: { dim: "kind", value: "voice" } }],
    daily: true,
    adopters: `sent one ${LAST_30}`,
  },
  {
    id: "location_messages",
    label: "Location messages",
    counts: [{ label: "Location messages", metric: "messages", only: { dim: "kind", value: "location" } }],
    daily: true,
    adopters: `sent one ${LAST_30}`,
  },
  {
    id: "check_ins",
    label: "Check-ins",
    counts: [{ label: "Check-ins", metric: "check_ins" }],
    daily: true,
    adopters: `checked in ${LAST_30}`,
  },
  {
    id: "places",
    label: "Places",
    counts: [
      { label: "Places created", metric: "places_created" },
      { label: "Arrivals and departures", metric: "place_events" },
    ],
    daily: false,
    adopters: "in a circle with a place now",
  },
  {
    id: "alerts",
    label: "Alerts",
    counts: [{ label: "Member alerts", metric: "member_alerts" }],
    daily: false,
    adopters: "have an alert rule turned on now",
  },
  {
    id: "sos",
    label: "SOS",
    counts: [{ label: "Alerts started", metric: "sos" }],
    daily: true,
    adopters: `started one ${LAST_30}`,
  },
  {
    id: "bubbles",
    label: "Approximate sharing",
    counts: [{ label: "Bubbles started", metric: "bubbles_started" }],
    daily: true,
    adopters: `started a bubble ${LAST_30}`,
  },
  {
    id: "pauses",
    label: "Pauses",
    counts: [{ label: "Pauses started", metric: "pauses_started" }],
    daily: true,
    adopters: `paused sharing ${LAST_30}`,
  },
  {
    id: "supervision",
    label: "Supervision",
    counts: [{ label: "Supervisions started", metric: "supervisions_started" }],
    daily: false,
    adopters: "supervised or a guardian now",
  },
  {
    id: "events",
    label: "Events",
    counts: [{ label: "Events created", metric: "events_created" }],
    daily: true,
    adopters: `in a live event ${LAST_30}`,
  },
  {
    id: "api_tokens",
    label: "Home Assistant and API tokens",
    counts: [{ label: "Tokens created", metric: "api_tokens_created" }],
    daily: true,
    adopters: `used one ${LAST_30}`,
  },
  {
    id: "avatars",
    label: "Profile photos",
    counts: [],
    daily: false,
    adopters: "have a photo now",
    adoptersMetric: "avatar_set",
  },
];

/** The metrics with adopters of their own, each once (read over the adopters' days). */
export function adopterReads(features: readonly Feature[] = FEATURES): AnalyticsMetric[] {
  return [...new Set(features.flatMap((feature) => (feature.adoptersMetric ? [feature.adoptersMetric] : [])))];
}

/** The metrics the counts read, each once, with the dim a read groups by (if any). */
export function countReads(features: readonly Feature[] = FEATURES): { metric: AnalyticsMetric; group?: string }[] {
  const reads = new Map<AnalyticsMetric, string | undefined>();
  for (const feature of features)
    for (const source of feature.counts) {
      const group = source.only?.dim ?? reads.get(source.metric);
      reads.set(source.metric, group);
    }
  return [...reads].map(([metric, group]) => (group ? { metric, group } : { metric }));
}

/**
 * A count source's values from its metric's read: the matching group, or
 * every group summed per bucket (null where nothing was built).
 */
export function countValues(source: CountSource, read: AnalyticsSeries | undefined): (number | null)[] {
  if (!read) return [];
  if (source.only) return seriesFor(read, source.only.dim, source.only.value);
  return sumSeries(read);
}

/** Every series of a read summed per bucket (null where every series is null, i.e. unbuilt). */
export function sumSeries(read: AnalyticsSeries): (number | null)[] {
  if (!read.series.length) return builtZeros(read);
  return read.buckets.map((_, i) => {
    let total: number | null = null;
    for (const s of read.series) {
      const v = s.values[i];
      if (v !== null && v !== undefined) total = (total ?? 0) + v;
    }
    return total;
  });
}

/** The latest bucket with a value: its index and value, or null. */
export function latest(values: readonly (number | null)[]): { index: number; value: number } | null {
  for (let i = values.length - 1; i >= 0; i--) {
    const value = values[i];
    if (value !== null && value !== undefined) return { index: i, value };
  }
  return null;
}

/** Sum of the known values (null when none is known). */
export function total(values: readonly (number | null)[]): number | null {
  const known = values.filter((v): v is number => v !== null);
  return known.length ? known.reduce((a, b) => a + b, 0) : null;
}

/** Mean of the known values (null when none is known). */
export function mean(values: readonly (number | null)[]): number | null {
  const known = values.filter((v): v is number => v !== null);
  return known.length ? known.reduce((a, b) => a + b, 0) / known.length : null;
}

export interface AdopterRow {
  feature: Feature;
  /** People on the snapshot day (null without one). */
  adopters: number | null;
  /** Their share of the monthly actives that day (null without both). */
  share: number | null;
  /** Average daily people over the range (null for features without daily counts, or nothing built). */
  daily: number | null;
  /** Each count source's total over the range. */
  counts: (number | null)[];
}

/**
 * The adopters table: `adopters`, `active30` and `ownAdopters` (a feature's
 * `adoptersMetric`) are daily reads over the same days, so a share always
 * divides one snapshot day's numbers; `users` (daily, so a short last week
 * or month weighs only its days) and `counts` cover the page's range.
 */
export function adopterRows(input: {
  adopters: AnalyticsSeries;
  active30: AnalyticsSeries;
  usersDaily: AnalyticsSeries;
  counts: ReadonlyMap<AnalyticsMetric, AnalyticsSeries>;
  ownAdopters?: ReadonlyMap<AnalyticsMetric, AnalyticsSeries>;
  features?: readonly Feature[];
}): { day: string | null; monthlyActive: number | null; rows: AdopterRow[] } {
  const features = input.features ?? FEATURES;
  // The latest day any feature has an adopters row (snapshots write every feature at once).
  const snapshot = latest(sumSeries(input.adopters));
  const index = snapshot?.index ?? null;
  const monthly = index === null ? null : (input.active30.series[0]?.values[index] ?? null);
  const rows = features.map((feature): AdopterRow => {
    const own = feature.adoptersMetric && input.ownAdopters?.get(feature.adoptersMetric);
    const values = feature.adoptersMetric
      ? own
        ? sumSeries(own)
        : []
      : seriesFor(input.adopters, "feature", feature.id);
    const adopters = index === null ? null : (values[index] ?? null);
    return {
      feature,
      adopters,
      share: adopters === null || !monthly ? null : adopters / monthly,
      daily: feature.daily ? mean(seriesFor(input.usersDaily, "feature", feature.id)) : null,
      counts: feature.counts.map((source) => total(countValues(source, input.counts.get(source.metric)))),
    };
  });
  return { day: index === null ? null : input.adopters.buckets[index]!, monthlyActive: monthly, rows };
}

/** The adopters CSV columns past the first five: each count source's label and exact total. */
export const MAX_COUNTS = Math.max(...FEATURES.map((f) => f.counts.length));
export function countCells(row: AdopterRow): (string | number | null)[] {
  return Array.from({ length: MAX_COUNTS }, (_, i) => {
    const source = row.feature.counts[i];
    return source ? [source.label, row.counts[i] ?? null] : [null, null];
  }).flat();
}
